import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import axios from 'axios';

import './ui-kit.css';
import './ds-color-primitives.css';
import './ds-spacing-primitives.css';
import './ds-colors.css';
import './ds-typography.css';
import './index.css';

import * as serviceWorker from './serviceWorker';
import { defaultConfig, globalConfig, globalConfigUrl } from './configuration/config';
import { api } from './api';
import { handleAuthentication, initKeycloak, keycloak } from './keycloak';
import { MixpanelProvider } from './wrappers/mixpanel';
import { initSentry } from './lib/sentry';
import { initHotjar } from './lib/hotjar';
import { routes } from './app/router';
import { MaintenanceRoute } from './routes/maintenance/maintenance.route';

axios
  .get(`${window.location.origin}/${globalConfigUrl}`, {
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    },
  })
  .then((response) => {
    globalConfig.config = response.data;

    if (process.env.NODE_ENV !== 'development') {
      initSentry();
    }

    if (globalConfig.config.environment === 'production' && globalConfig.config.hotjar) {
      initHotjar(globalConfig.config.hotjar);
    }

    api.init();
    return initKeycloak(globalConfig.config.keycloak);
  })
  .catch((e) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Failed to load global configuration from '${globalConfigUrl}', using the default configuration instead:`,
        defaultConfig,
      );
      globalConfig.config = defaultConfig;
      return initKeycloak(defaultConfig.keycloak);
    } else {
      const errorMessage =
        'Error while fetching global config, the App wil not be rendered. (This is NOT a React error.)';
      console.error(errorMessage, `Have you provided the config file '${globalConfigUrl}'?`, e);
      return <p style={{ color: 'red', textAlign: 'center' }}>{errorMessage}</p>;
    }
  })
  .then(async () => {
    try {
      const urlParams = new URLSearchParams(window.location.hash.substring(1));
      const hasError = urlParams.has('error');
      const errorType = urlParams.get('error');
      
      const isOAuthCallback = urlParams.has('code') || 
                              urlParams.has('access_token') || 
                              urlParams.has('state');
      
      const token = isOAuthCallback ? undefined : localStorage.getItem('kc_token') || undefined;
      const refreshToken = isOAuthCallback ? undefined : localStorage.getItem('kc_refreshToken') || undefined;
      
      const onLoad = (hasError && errorType === 'login_required') ? 'login-required' : 
                     hasError ? 'login-required' : 'check-sso';
      
      const authenticated = await keycloak.init({
        onLoad,
        checkLoginIframe: false,
        token,
        refreshToken,
        redirectUri: window.location.origin + '/',
        pkceMethod: 'S256',
      });
      
      if (hasError && !authenticated) { 
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      const shouldContinue = await handleAuthentication(authenticated, isOAuthCallback);
      
      return shouldContinue && authenticated;
    } catch (error) {
      console.error('Keycloak initialization error:', error);
      keycloak.login({ redirectUri: window.location.origin + '/' });
      return false;
    }
  })
  .then(async (authenticated) => {
    if (!authenticated) {
      console.log('Not authenticated, waiting for redirect...');
      return;
    }
    
    const root = createRoot(document.getElementById('root')!);
    const router = createBrowserRouter(routes);

    try {
      const { data: status } = await api.get('/');

      root.render(
        <MixpanelProvider>
          {status.isInMaintenance ? <MaintenanceRoute /> : <RouterProvider router={router} />}
        </MixpanelProvider>,
      );
    } catch (error) {
      console.error('Failed to fetch app status:', error);
      root.render(
        <MixpanelProvider>
          <RouterProvider router={router} />
        </MixpanelProvider>,
      );
    }
  })
  .catch((error) => {
    console.error('Unhandled error during app initialization:', error);
    const root = createRoot(document.getElementById('root')!);
    root.render(
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Application Error</h1>
        <p>Failed to initialize the application. Please refresh the page.</p>
        {process.env.NODE_ENV === 'development' && <pre>{JSON.stringify(error, null, 2)}</pre>}
      </div>,
    );
  });

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
